/**
 * CauseRecordsDialog - Dialog showing records for a specific root cause
 *
 * Uses the shared RecordsTable component for consistent record display.
 * Clicking a record navigates in-app to the record detail, carrying a
 * referrer so "Back" returns to the report.
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../../../ui/dialog';
import { Badge } from '../../../../../ui/badge';
import { RecordsTable } from '../molecules';
import { useCauseRecords } from '../../hooks';
import type { CauseRecordsDialogProps } from '../../types';
import { useNavigation } from '../../../../../../contexts/NavigationContext';

export function CauseRecordsDialog({
  isOpen,
  onClose,
  causeId,
  causeName,
  datasetId,
  filters,
  onCauseClick,
}: Omit<CauseRecordsDialogProps, 'onRecordClick'>) {
  const { navigateWithReferrer } = useNavigation();
  const [page, setPage] = useState(1);

  // Reset page when dialog opens or cause changes
  useEffect(() => {
    if (isOpen) {
      setPage(1);
    }
  }, [isOpen, causeId]);

  const { data, isLoading, isError } = useCauseRecords(
    datasetId,
    causeId,
    filters,
    page,
    isOpen && !!causeId
  );

  const handleRecordClick = (recordId: string) => {
    navigateWithReferrer(`/v4/records/${datasetId}/${recordId}`);
  };

  const handlePreviousPage = () => {
    setPage((p) => Math.max(1, p - 1));
  };

  const handleNextPage = () => {
    if (data && page < data.totalPages) {
      setPage((p) => p + 1);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[90vw] sm:w-[1200px] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {causeName} Records
            {data && (
              <Badge variant="secondary" className="ml-2">
                {data.totalRecords} total
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Records associated with this cause
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[340px] overflow-y-auto overflow-x-hidden">
          {isError ? (
            <div className="py-8 text-center text-red-500">
              Failed to load records. Please try again.
            </div>
          ) : (
            <RecordsTable
              records={data?.records ?? []}
              onRecordClick={handleRecordClick}
              onCauseClick={onCauseClick}
              isLoading={isLoading}
              emptyMessage="No records found for this root cause."
              loadingRows={5}
              stickyHeader
              actionIcon="chevron"
              pagination={
                data && data.totalPages > 1
                  ? {
                      page: data.page,
                      totalPages: data.totalPages,
                      totalRecords: data.totalRecords,
                      onPreviousPage: handlePreviousPage,
                      onNextPage: handleNextPage,
                      isLoading,
                    }
                  : undefined
              }
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
